import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  ConditionDTO,
  ContainersDTO,
  CustomLayerDTO,
  DropDTO,
  ExperimentInfoDTO,
  GridDTO,
  GridLayerDTO,
  LayerDetailsDTO,
  LayerDTO,
  LayerTypeDTO,
  PlateIngredientDTO,
  ProteinLayerDTO,
  RandomLayerDTO,
  RandomLayerIngredientGroupDTO,
  SaveCanvasItemsDTO,
  SaveExperimentDTO,
  SaveImagingScheduleDTO,
  SavePlateDTO,
  ScreenLayerDTO,
  WellIngDTO,
  SeedLayerDetailsDTO,
  ImportIngredientsDTO,
  FragmentLayerDTO,
  DropLayerDTO,
  DropLayerFragment,
  ContainerDropDTO,
  SavePlateInfoDto,
  BaseDriverPropertyDTO,
  WellLayerDTO,
  WellLayerIngredientDTO,
} from 'business/models/Models';
import PlateDtoClient from 'business/models/PlateDtoClient';
import {
  getLinkedLayerGridDto,
  isOverlappingLayer,
} from 'business/plateviewer/GenerateUtil';
import {
  getExperimentInfoIsDirty,
  getImageSchedulesIsDirty,
} from 'components/customComponents/Experiment/ImagingSchedule/ImagingScheduleData';
import { setPlateSize, getExperimentPlateSize } from 'components/main/Experiment';
import EIconPanels from 'enums/EIconPanels';
import { EGridVaryAlong } from 'enums/ELayersView';
import { cloneDeep } from 'lodash';
import {
  GetContainers,
  GetDefaultPlatePadding,
  GetIngredients,
  GetPlatePadding,
  GetUnifiedExperimentCanvasHeight,
  GetUnifiedExperimentCanvasWidth,
  SetPlatePadding,
  getNestedObject,
  getNewId,
  isCustomScheduleSelected,
} from './Global';
import { RMRectangle } from 'business/models/CustomModels';
import { PlatePosition, PlateStructure } from 'components/canvas/constants';
import { Dimensions } from 'constants/types';
import {
  Containers,
  Drop,
  Plate,
  PlateImageStructure,
  PlateIngredient,
  SaveImagingSchedule,
  WellIng,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { WellDisplayMode } from 'components/well/constants';
import { EProductEditions } from 'enums/EProductEditions';

export const getNewPlate = (
  plateNumber: number,
  container: Containers,
  row: number,
  col: number,
  wellCount: number,
) => {
  const id = getNewId();

  const ingredients: PlateIngredient = {
    containerMaxVolume: container.maxWellVolume,
    maxWellVolume: 0,
    containerDropList: container.containerDrops,
    wellList: [],
    plateId: id,
    hasError: false,
    hasWarning: false,
  };

  let initialLetterCount = 0;
  let mainLetterCount = 0;
  let finalLetter = '';
  let columnNumber = 0;
  const wellList: WellIng[] = [];
  for (let wellNumber = 1; wellNumber <= wellCount; wellNumber++) {
    if ((mainLetterCount + 10).toString(36).toUpperCase() === '10') {
      finalLetter = (initialLetterCount + 10).toString(36).toUpperCase();
      mainLetterCount = 0;
      initialLetterCount++;
    }
    let letter = (mainLetterCount + 10).toString(36).toUpperCase();
    letter = finalLetter + letter;

    if (wellNumber % container.cols == 0) {
      mainLetterCount++;
    }

    const _wellId = getNewId();
    columnNumber++;
    const newWell: WellIng = {
      columnNumber,
      rowLetter: letter,
      wellNumber,
      wellVolume: 0,
      wellWaterVolume: 0,
      wellId: _wellId,
      wellLayerList: [],
      dropList: [],
      plateId: id,
      hasError: false,
      hasWarning: false,
    };
    if (container.containerDrops) {
      const dropList = container.containerDrops.map((d) => {
        return {
          dropId: getNewId(),
          dropLayerList: [],
          dropNumber: d.dropNumber,
          dropProteinVolume: 0,
          dropSeedVolume: 0,
          dropFragmentVolume: 0,
          dropWellVolume: 0,
          proteinFormulationID: null,
          seedIDs: [],
          volume: 0,
          waterVolume: 0,
          wellId: _wellId,
        } as Drop;
      });
      newWell.dropList = dropList;
    } else {
      newWell.dropList = [];
    }

    wellList.push(newWell);
    ingredients.wellList = wellList;

    if (wellNumber % container.cols == 0) {
      columnNumber = 0;
    }
  }

  const newPlate: Plate = {
    plateId: id,
    plateNumber: plateNumber,
    rows: container.rows,
    cols: container.cols,
    plateBarName: 'Plate ' + plateNumber,
    rowPos: row,
    colPos: col,
    ingredients: ingredients,
    availableHeight: 0,
    availableWidth: 0,
    annotationLoaded: false,
    isSeeded: false,
  };
  return newPlate;
};

export const getDeletedPlates = (prevPlates: Plate[], curPlates: Plate[]) => {
  const deletedPlates = prevPlates.filter(
    (plate) => !curPlates.find((p) => p.plateId === plate.plateId),
  );
  const deletedPlateIds = new Array<number>();
  deletedPlates.forEach((p) => {
    if (p.plateId && p.plateId > 0) {
      deletedPlateIds.push(p.plateId);
    }
  });
  return deletedPlateIds;
};

export const createContainerFromExp = (expDto: ExperimentDtoClient) => {
  const container = new ContainersDTO();
  container.cols = expDto.containerCols;
  container.rows = expDto.containerRows;
  container.defaultWellVolume = expDto.defWellVolume;
  container.containerDrops = expDto.containerDrops;
  container.defaultAdditiveVolume = expDto.containerDefAdditiveVolume;
  container.maxWellVolume = expDto.containerMaxWellVolume;
  container.defaultDropVolume = expDto.containerDefDropVolume;
  container.maxDropVolume = expDto.containerMaxDropVolume;
  return container;
};

export const isPlateHaveLayer = (plate: Plate, layers: LayerDTO[]) => {
  const plateStartX = (plate.colPos - 1) * plate.cols;
  const plateEndX = plateStartX + plate.cols;
  const plateStartY = (plate.rowPos - 1) * plate.rows;
  const plateEndY = plateStartY + plate.rows;
  for (const lyr of layers) {
    if (
      lyr.x < plateEndX &&
      lyr.x + lyr.width + 1 > plateStartX &&
      lyr.y < plateEndY &&
      lyr.y + lyr.height + 1 > plateStartY
    ) {
      return true;
    }
  }
  return false;
};

export const generateEmptyCanvasItems = (expData: ExperimentDtoClient) => {
  const container = createContainerFromExp(expData);
  if (expData.rowCount >= expData.colCount) {
    for (let row = 1; row <= expData.rowCount; row++) {
      for (let col = 1; col <= expData.colCount; col++) {
        const index = expData.plateDto.findIndex(
          (p) => p.rowPos === row && p.colPos === col,
        );
        if (index >= 0) {
          generateMissingDrops(expData.plateDto[index], container);
          continue;
        }
        const plateNum = (row - 1) * expData.colCount + col;
        const newPlate = getNewPlate(
          plateNum,
          container,
          row,
          col,
          container.rows * container.cols,
        );
        expData.plateDto.push(newPlate);
      }
    }
  } else {
    for (let col = 1; col <= expData.colCount; col++) {
      for (let row = 1; row <= expData.rowCount; row++) {
        const index = expData.plateDto.findIndex(
          (p) => p.rowPos === row && p.colPos === col,
        );
        if (index >= 0) {
          generateMissingDrops(expData.plateDto[index], container);
          continue;
        }
        const plateNum = (row - 1) * expData.colCount + col;
        const newPlate = getNewPlate(
          plateNum,
          container,
          row,
          col,
          container.rows * container.cols,
        );
        expData.plateDto.push(newPlate);
      }
    }
  }
  return expData;
};

export const createSaveExpDTO = (
  exp: ExperimentDtoClient,
  plates: SavePlateDTO[],
  deletedplateIds: number[],
  pDeletedLayers: LayerDTO[],
  importedIngredients?: ImportIngredientsDTO,
) => {
  const delLayers = Array<LayerDTO>();
  for (const delLyr of pDeletedLayers) {
    if (delLyr.id > 0) {
      delLayers.push(delLyr);
    }
  }
  const canvasItems: SaveCanvasItemsDTO = new SaveCanvasItemsDTO();
  canvasItems.deletedPlateIds = deletedplateIds;
  canvasItems.deletedLayers = delLayers;
  canvasItems.plateInfo = exp.plateDispensedDetails?.length
    ? exp.plateDispensedDetails.map((info) => SavePlateInfoDto.fromJS(info))
    : undefined;
  canvasItems.colCount = exp.colCount;
  canvasItems.rowCount = exp.rowCount;
  const canvasBodyParam = SaveCanvasItemsDTO.fromJS(canvasItems);
  canvasBodyParam.plates = plates.map((plate) => {
    return new SavePlateDTO({
      ...plate,
      ingredients: new PlateIngredientDTO({
        ...plate.ingredients,
        containerDropList: plate.ingredients.containerDropList?.map((drop) => {
          return new ContainerDropDTO({
            ...drop,
          });
        }),
        wellList: plate.ingredients.wellList.map((well) => {
          return new WellIngDTO({
            ...well,
            wellLayerList: well.wellLayerList?.map((layer) => {
              return new WellLayerDTO({
                ...layer,
                wellLayerIngredientList: layer.wellLayerIngredientList?.map(
                  (ingredient) => {
                    return new WellLayerIngredientDTO({
                      ...ingredient,
                      ingredientStock: null, // Don't need to send Ingredient object, just Ids are enough
                      highPhIngStock: null,
                      ingredientStockIngredient: null,
                      highPhIngStockIngredient: null,
                    });
                  },
                ),
              });
            }),
            dropList:
              well.dropList.map((drop) => {
                return new DropDTO({
                  ...drop,
                  dropLayerList:
                    drop.dropLayerList?.map((dropLayer) => {
                      return new DropLayerDTO({
                        ...dropLayer,
                        dropLayerFragmentList: dropLayer.dropLayerFragmentList?.map(
                          (dropLayerFragment) => {
                            return new DropLayerFragment({ ...dropLayerFragment });
                          },
                        ),
                      });
                    }) || [],
                });
              }) || [],
          });
        }),
      }),
    });
  });
  canvasBodyParam.layers = exp.layers.map((l) => {
    if (l.type === LayerTypeDTO.Screen) {
      (l.layerDetail as ScreenLayerDTO).screenLot.experiment = null;
    }
    l.layerDetail = LayerDetailsDTO.fromJS(l.layerDetail.toJSON());
    return l;
  });
  canvasBodyParam.deletedLayers = delLayers.map((l) => {
    if (l.type === LayerTypeDTO.Screen) {
      const lyrDetail = new ScreenLayerDTO();
      lyrDetail.init(l.layerDetail);
      lyrDetail.screenLot.experiment = null;
      l.layerDetail = lyrDetail;
    }
    l.layerDetail = LayerDetailsDTO.fromJS(l.layerDetail.toJSON());
    return l;
  });

  const saveExpDTO = new SaveExperimentDTO();
  saveExpDTO.expId = exp.experimentId;
  saveExpDTO.experimentCanvas = canvasBodyParam;
  saveExpDTO.importIngredients = importedIngredients;
  if (getImageSchedulesIsDirty()) {
    const saveImagingScheduleDTO: SaveImagingSchedule = {
      selectedImagingScheduleId: exp.imagingSchedule.selectedImagingScheduleId,
      imagingScheduleEntries: exp.imagingSchedule.imagingScheduleEntries,
      isChoosedCustom: isCustomScheduleSelected(exp.imagingSchedule),
    };
    saveExpDTO.imagingSchedule = SaveImagingScheduleDTO.fromJS(saveImagingScheduleDTO);
  }
  if (getExperimentInfoIsDirty()) {
    saveExpDTO.experimentInfo = ExperimentInfoDTO.fromJS(exp.info as ExperimentInfoDTO);
    if (saveExpDTO?.experimentInfo?.name) {
      saveExpDTO.experimentInfo.name = saveExpDTO.experimentInfo.name.trim();
    }
    if (!saveExpDTO.experimentInfo.reducingAgent) {
      saveExpDTO.experimentInfo.reducingAgent = 0;
    }
  }
  if (exp.dispensingProps && exp.dispensingProps.length > 0) {
    saveExpDTO.dispensingProps = exp.dispensingProps.map((item) =>
      BaseDriverPropertyDTO.fromJS(item),
    );
  }
  return saveExpDTO;
};

export const initNewLayer = (
  layerType: string,
  newLayer: LayerDTO,
  expId: number,
  defaultVol: number,
  layers: LayerDTO[],
  containerMaxDrops: number,
) => {
  let layerName = '';
  switch (layerType) {
    case EIconPanels.GridLayer:
      newLayer.type = LayerTypeDTO.Grid;
      const gridlayerDetail: GridLayerDTO = new GridLayerDTO();
      gridlayerDetail.id = getNewId();
      gridlayerDetail.grid = new GridDTO();
      gridlayerDetail.grid.id = getNewId();
      gridlayerDetail.grid.gridIngredient = [];
      gridlayerDetail.grid.experimentId = expId;
      gridlayerDetail.grid.startWellVolume = defaultVol;
      gridlayerDetail.grid.stopWellVolume = defaultVol;
      gridlayerDetail.grid.wellVolumeVaryAlong = 1;
      gridlayerDetail.layerId = newLayer.id;
      gridlayerDetail.gridId = gridlayerDetail.grid.id;
      gridlayerDetail.subGridNumber = 1;
      gridlayerDetail.grid.gridIngredient = [];
      gridlayerDetail.grid.startWellVolume = defaultVol;
      gridlayerDetail.grid.wellVolumeVaryAlong = EGridVaryAlong.NONE;
      newLayer.layerDetail = gridlayerDetail;

      layerName = 'Grid Layer';
      break;
    case EIconPanels.CustomLayer:
      newLayer.type = LayerTypeDTO.ConditionList;
      const customLayerDetail: CustomLayerDTO = new CustomLayerDTO();
      customLayerDetail.experimentId = expId;
      customLayerDetail.id = getNewId();
      customLayerDetail.layerId = newLayer.id;
      customLayerDetail.wellVolume = defaultVol;
      customLayerDetail.condition = [];
      const cond: ConditionDTO = new ConditionDTO();
      cond.id = getNewId();
      cond.conditionListLayerId = customLayerDetail.id;
      cond.conditionIngredient = [];
      cond.conditionNumber = 1;
      customLayerDetail.condition.push(cond);
      newLayer.layerDetail = customLayerDetail;
      layerName = 'Custom Layer';
      break;
    case EIconPanels.ProtienLayer:
      newLayer.type = LayerTypeDTO.Protein;
      const proteinLayerDetail: ProteinLayerDTO = new ProteinLayerDTO();
      proteinLayerDetail.layerId = newLayer.id;
      proteinLayerDetail.experimentId = expId;
      proteinLayerDetail.id = getNewId();
      newLayer.layerDetail = proteinLayerDetail;
      newLayer.targetDrop = getDropLayerTarget(
        newLayer,
        layers,
        containerMaxDrops,
        LayerTypeDTO.Protein,
      );
      layerName = 'Protein Layer';
      newLayer.volumeBasedAddition = true;
      break;
    case EIconPanels.RandomLayer:
      newLayer.type = LayerTypeDTO.Random;
      const ingTypes = GetIngredients().ingredientType;
      const ingGroups: RandomLayerIngredientGroupDTO[] = [];
      ingTypes.map((it) => {
        const ingType: RandomLayerIngredientGroupDTO =
          new RandomLayerIngredientGroupDTO();
        ingType.id = getNewId();
        ingType.name = it.name;
        ingType.randomLayerGenerationList = [];
        ingType.randomLayerGroupIngredient = [];
        ingGroups.push(ingType);
      });
      const randomLayerDetail = new RandomLayerDTO();
      randomLayerDetail.randomLayerExcludeList = [];
      randomLayerDetail.randomLayerIngredientGroup = ingGroups;
      randomLayerDetail.experimentId = expId;
      randomLayerDetail.id = getNewId();
      randomLayerDetail.layerId = newLayer.id;
      randomLayerDetail.wellVolume = defaultVol;
      randomLayerDetail.gridSteps = 10;
      newLayer.layerDetail = randomLayerDetail;
      layerName = 'Random Layer';
      break;
    case EIconPanels.ScreenLayer:
      newLayer.type = LayerTypeDTO.Screen;
      const screenLayerDetail = new ScreenLayerDTO();
      screenLayerDetail.layerId = newLayer.id;
      newLayer.layerDetail = screenLayerDetail;
      newLayer.volumeBasedAddition = true;
      layerName = 'Screen Layer';
      break;
    case EIconPanels.FragmentLayer:
      newLayer.type = LayerTypeDTO.Fragment;
      const fragmentLayerDetail = new FragmentLayerDTO();
      fragmentLayerDetail.id = getNewId();
      fragmentLayerDetail.layerId = newLayer.id;
      newLayer.layerDetail = fragmentLayerDetail;
      newLayer.targetDrop = getDropLayerTarget(
        newLayer,
        layers,
        containerMaxDrops,
        LayerTypeDTO.Fragment,
      );
      newLayer.volumeBasedAddition = true;
      newLayer.volume = 0;
      layerName = 'Fragment Layer';
      break;
    case EIconPanels.SeedingLayer:
      newLayer.type = LayerTypeDTO.Seed;
      const seedLayerDetailsDto: SeedLayerDetailsDTO = new SeedLayerDetailsDTO();
      seedLayerDetailsDto.layerId = newLayer.id;
      seedLayerDetailsDto.seedDropVolumeVaryAlong = EGridVaryAlong.NONE;
      seedLayerDetailsDto.startSeedDropVolume = defaultVol;
      seedLayerDetailsDto.stopSeedDropVolume = defaultVol;
      newLayer.layerDetail = seedLayerDetailsDto;
      newLayer.targetDrop = getDropLayerTarget(
        newLayer,
        layers,
        containerMaxDrops,
        LayerTypeDTO.Seed,
      );
      layerName = 'Seed Layer';
      newLayer.volumeBasedAddition = true;
      break;
    default:
      newLayer.type = 1;
      layerName = 'Grid Layer';
      break;
  }
  return layerName;
};

export const verifyLayerDetails = (layer: any) => {
  const mappedLayerTypeKeys = {
    [LayerTypeDTO.Grid]: ['layerDetail', 'grid', 'gridIngredient'],
    // [LayerTypeDTO.ConditionList]: ['conditionIngredient', 'condition'],
    [LayerTypeDTO.Protein]: ['layerDetail', 'proteinLayerProteinFormulation'],
    [LayerTypeDTO.Random]: ['layerDetail', 'randomLayerIngredientGroup'],
    [LayerTypeDTO.Screen]: ['layerDetail', 'screenLot'],
    [LayerTypeDTO.Seed]: ['layerDetail', 'seedId'],
    [LayerTypeDTO.Fragment]: ['layerDetail', 'fragmentScreen'],
  };
  if (!mappedLayerTypeKeys[layer?.type]) {
    return 'notfound';
  }

  // @ts-ignore
  const keyValue = getNestedObject(layer, ...mappedLayerTypeKeys[layer?.type]);

  if (layer.type === LayerTypeDTO.Random && keyValue?.length) {
    const ingredientsGroupList = keyValue as RandomLayerIngredientGroupDTO[];
    return ingredientsGroupList?.some(
      (_value: RandomLayerIngredientGroupDTO) => _value.randomLayerGenerationList?.length,
    );
  }

  return keyValue;
};

export const expandPlateRow = (expData: ExperimentDtoClient, newRowCount: number) => {
  expData.rowCount = newRowCount;
  const containers = GetContainers() as Containers[];
  const cont = containers.find((c) => c.id == expData.info.containerId);
  const wellCount = cont.rows * cont.cols;
  for (let row = expData.info.plateRowCount + 1; row <= newRowCount; row++) {
    for (let col = 1; col <= expData.colCount; col++) {
      const plateNum = (row - 1) * expData.colCount + col;
      const newPlate = getNewPlate(plateNum, cont, row, col, wellCount);
      expData.plateDto.push(newPlate);
    }
  }
  expData.plateDto.forEach((plate) => {
    plate.plateNumber = (plate.rowPos - 1) * expData.colCount + plate.colPos;
    if (plate.plateId < 0) {
      plate.plateBarName = 'Plate ' + plate.plateNumber;
    }
  });
  expData.plateDto.sort(function (a, b) {
    return a.plateNumber - b.plateNumber;
  });
  expData.info.plateRowCount = newRowCount;
  expData.info.isDirty = true;
  expData.rowCount = newRowCount;
  expData.isDirty = true;
  for (const plate of expData.plateDto) {
    plate.ingredients = cloneDeep(plate.ingredients) as PlateIngredient;
  }
  expData.layers && expData.layers.forEach((layer) => (layer.dirty = true));
  const { plateWidth, plateHeight } = getExperimentPlateSize(expData);
  setPlateSize(expData, plateWidth, plateHeight);
};

export function getLayerVolume(layer: LayerDTO, allLayers: LayerDTO[]): number {
  switch (layer.type) {
    case LayerTypeDTO.Grid:
      return getLinkedLayerGridDto(allLayers, layer.layerDetail).startWellVolume;
    case LayerTypeDTO.Screen:
      return layer.volume;
    case LayerTypeDTO.Random:
    case LayerTypeDTO.ConditionList:
      return (layer.layerDetail as CustomLayerDTO).wellVolume;
    default:
      return 0;
  }
}

export { isOverlappingLayer };

export const getDropLayerTarget = (
  newLayer: LayerDTO,
  layers: LayerDTO[],
  containerMaxDrops: number,
  layerType: LayerTypeDTO,
): number => {
  let targetDrop = 1;
  layers.forEach((lyr) => {
    if (
      lyr.type === layerType &&
      isOverlappingLayer(lyr, newLayer) &&
      lyr.targetDrop === targetDrop
    ) {
      targetDrop++;
    }
  });
  return Math.min(containerMaxDrops, targetDrop);
};

export const getPlateLocationByNextWellLocation = (
  currentPlatePosition,
  startWell,
  rowsInPlate,
  columnsInPlate,
  nextWellColumn = null,
  nextWellRow = null,
) => {
  const newPlatePosition = { ...currentPlatePosition };
  if (nextWellColumn != null) {
    newPlatePosition.rowPos = Math.floor(nextWellColumn / rowsInPlate) + 1;
  }
  if (nextWellRow != null) {
    newPlatePosition.colPos =
      Math.floor((startWell.x + nextWellRow) / columnsInPlate) + 1;
  }
  return newPlatePosition;
};

export const generateCrystalColorHex = (number: number) => {
  const hue = number * 9 * 520;
  const r = Math.floor(hue * 360 + 128).toString(16);
  const g = Math.floor((hue + 120) * 127 + 128).toString(16);
  const b = Math.floor((hue + 240) * 127 + 128).toString(16);
  const color = r + g + b;
  return `#${color.slice(0, 6)}`;
};

export const getPlateStructure = (
  platesPosition: PlatePosition[] | PlateDtoClient[],
  plateWidth: number,
  plateHeight: number,
  containerRows: number,
  containerCols: number,
  wellDimensions?: Dimensions,
): PlateStructure[] => {
  const allPlatesStructure: PlateStructure[] = [];
  platesPosition.forEach((platesPosition) => {
    const { colPos, rowPos } = platesPosition;
    const startX = plateWidth * (colPos - 1) + (GetPlatePadding() / 2) * colPos;
    const startY = plateHeight * (rowPos - 1) + (GetPlatePadding() / 2) * rowPos;
    const plateStructure: PlateStructure = {
      startX,
      startY,
      availableWidth: plateWidth,
      availableHeight: plateHeight,
      width: plateWidth,
      height: plateHeight,
      stx: 0,
      sty: 0,
      endx: containerCols,
      endy: containerRows,
      wellRects: [],
      wellRectsPadded: [],
    };
    setWellRects(plateStructure, containerRows, containerCols, wellDimensions);
    allPlatesStructure.push(plateStructure);
  });
  return allPlatesStructure;
};

export function setWellRects(
  plate: PlateStructure,
  rows: number,
  cols: number,
  wellDimensions?: Dimensions,
) {
  const _wellDimensions: Dimensions = wellDimensions?.width
    ? wellDimensions
    : { width: plate.width / cols, height: plate.height / rows };

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = plate.startX + col * _wellDimensions.width;
      const y = plate.startY + row * _wellDimensions.height;
      const w = _wellDimensions.width;
      const h = _wellDimensions.height;

      const actualWellRectangle: RMRectangle = { x, y, w, h };
      const pad = actualWellRectangle.w * 0.05;
      const wellRectangle: RMRectangle = {
        x: actualWellRectangle.x + pad,
        y: actualWellRectangle.y + pad,
        w: actualWellRectangle.w - pad * 2,
        h: actualWellRectangle.h - pad * 2,
      };
      plate.wellRectsPadded.push(wellRectangle);
      plate.wellRects.push(actualWellRectangle);
    }
  }
}

export const getPlateSize = (
  parentDivWidth: number,
  rowCount: number,
  colCount: number,
  containerRows: number,
  containerCols: number,
) => {
  let plateWidth = 0,
    plateHeight = 0;
  const availableWidthPerPlate =
    GetUnifiedExperimentCanvasWidth(parentDivWidth) / colCount;
  const availableHeightPerPlate = GetUnifiedExperimentCanvasHeight() / rowCount;
  const paddedPlateWidth = availableWidthPerPlate - GetDefaultPlatePadding();
  const paddedPlateHeight = availableHeightPerPlate - GetDefaultPlatePadding();
  if (colCount > rowCount) {
    plateWidth = paddedPlateWidth;
    plateHeight = (paddedPlateWidth * containerRows) / containerCols;

    // Adjust the width and height only if the calculated height is reasonable
    if (plateHeight > paddedPlateHeight) {
      plateHeight = paddedPlateHeight;
      plateWidth = (paddedPlateHeight / containerRows) * containerCols;
    }
  } else {
    plateHeight = paddedPlateHeight;
    plateWidth = (paddedPlateHeight / containerRows) * containerCols;

    // Adjust the width and height only if the calculated width is reasonable
    if (plateWidth > paddedPlateWidth) {
      plateWidth = paddedPlateWidth;
      plateHeight = (paddedPlateWidth * containerRows) / containerCols;
    }
  }
  const wellRectWidth = plateWidth / containerCols;
  // Need to add a well rect width in default padding because of plate labels and need to minus it from the plate width
  // Because the plate padding will later be used in label drawings etc. We cannot make it a static number because it may create an issue in case of multiplate
  SetPlatePadding(wellRectWidth + GetDefaultPlatePadding());
  plateWidth -= wellRectWidth;
  plateHeight -= wellRectWidth;
  return { plateWidth, plateHeight };
};

export const wellComparisonPredicate = (
  well1: WellNavigationDetails,
  well2: WellNavigationDetails,
) => well1.plateID === well2.plateID && well1.wellNum === well2.wellNum;

export const generateMissingDrops = (plate: Plate, container: ContainersDTO) => {
  plate.ingredients.wellList?.forEach((well) => {
    container.containerDrops?.forEach((d) => {
      if (!well.dropList.find((drop) => drop.dropNumber === d.dropNumber)) {
        well.dropList.push({
          dropId: getNewId(),
          dropLayerList: [],
          dropNumber: d.dropNumber,
          dropProteinVolume: 0,
          dropSeedVolume: 0,
          dropFragmentVolume: 0,
          dropWellVolume: 0,
          proteinFormulationID: null,
          seedIDs: [],
          volume: 0,
          waterVolume: 0,
          wellId: well.wellId,
        } as Drop);
      }
    });
    well.dropList.sort((a, b) => a.dropNumber - b.dropNumber);
  });
};

export const getAllWellDetails = (
  wellDtails: WellNavigationDetails,
  plates: Plate[],
  totalWellsOfPlate: number,
) => {
  const allWellDetails: WellNavigationDetails[] = [];
  for (let plateIndex = 0; plateIndex < plates.length; plateIndex++) {
    for (let wellIndex = 0; wellIndex < totalWellsOfPlate; wellIndex++) {
      allWellDetails.push({
        ...wellDtails,
        plateNum: plateIndex + 1,
        plateID: plates[plateIndex].plateId,
        wellNum: wellIndex + 1,
      });
    }
  }
  return allWellDetails;
};
export const getPlatesImagesStructureData = (exp: ExperimentDtoClient) => {
  const plateImagesStructure: PlateImageStructure[] = [];
  const { plateWidth, plateHeight } = getPlateSize(
    100,
    exp.rowCount,
    exp.colCount,
    exp.containerRows,
    exp.containerCols,
  );
  exp?.plateDto?.forEach((plate) => {
    const startX =
      plateWidth * (plate.colPos - 1) + (GetPlatePadding() / 2) * plate.colPos;
    const startY =
      plateHeight * (plate.rowPos - 1) + (GetPlatePadding() / 2) * plate.rowPos;
    plateImagesStructure.push({
      plateId: plate.plateId,
      plateNumber: plate.plateNumber,
      startX: startX,
      startY: startY,
      width: plateWidth,
      height: plateHeight,
      imageCompositeKeyString: null,
    });
  });
  return plateImagesStructure;
};

export const wellDropCompareMode = [
  WellDisplayMode.Well,
  WellDisplayMode.Compare,
  WellDisplayMode.Drop,
];

export const buildExperimentDropPath = (params: {
  expId: number;
  plateID: number;
  wellNum: number;
  dropNumber: number;
  cpId: number;
  regionName: string;
  imageBatchId: number;
  wellMode: WellDisplayMode;
}): string => {
  const {
    expId,
    plateID,
    wellNum,
    dropNumber,
    cpId,
    regionName,
    imageBatchId,
    wellMode,
  } = params;
  return `/exp/${expId}/${plateID}/${wellNum}/${dropNumber}/${cpId}/${regionName}/${imageBatchId}/${wellMode}`;
};

export const updateDropPath = (
  path,
  updates: { index: number; value: string | number }[],
  navigate: (path: string) => void,
) => {
  const parts = path.split('/');
  if (wellDropCompareMode.includes(parts[parts.length - 1])) {
    updates.forEach(({ index, value }) => {
      if (wellDropCompareMode.includes(parts[parts.length - 1])) {
        parts[index] = value;
      }
    });
    navigate(parts.join('/'));
  }
};

export const removeExperimentFromScreen = (layers: LayerDTO[]) => {
  layers.forEach((layer) => {
    if (layer.type == LayerTypeDTO.Screen) {
      (layer.layerDetail as ScreenLayerDTO).screenLot.experiment = null;
    }
  });
  return layers;
};

export const ProductEditionAllowedLayersMap = {
  [EProductEditions.PlateViewer]: [
    LayerTypeDTO.Protein,
    LayerTypeDTO.Seed,
    LayerTypeDTO.Screen,
    LayerTypeDTO.Fragment,
  ],
};

export const TreeNode = {
  TreeNodeNameMaxLength: 64,
} as const;
